import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { musicService } from "@/services/musicService";
import type { Album, Artist, LibraryKind, Track } from "@/types/types";

interface LibraryCtx {
  liked: { tracks: Set<string>; albums: Set<string>; artists: Set<string> };
  toggle: (kind: LibraryKind, id: string) => Promise<void>;
  isLiked: (kind: LibraryKind, id: string) => boolean;
  refresh: () => Promise<void>;
  likedTracks: Track[];
  likedAlbums: Album[];
  likedArtists: Artist[];
}

export const LibraryContext = createContext<LibraryCtx | null>(null);

const PAGE_SIZE = 100; // first batch — kept small so the page paints fast

/** Loads a Paged<T> endpoint lazily: the first 100 items land in `setState`
 * right away (so callers awaiting this — e.g. toggle() re-syncing after a
 * like — don't block on someone's entire library), then the rest streams in
 * page by page in the background, appended as it arrives. `gen`/`genRef`
 * cancel a stale background chain if refresh() is called again (e.g. rapid
 * likes) before it finishes, so an old chain can't clobber a newer one. */
async function loadLazy<T>(
  loader: (offset: number, limit: number) => Promise<{ items: T[]; total: number }>,
  setState: (updater: (cur: T[]) => T[]) => void,
  genRef: { current: number },
  gen: number,
): Promise<void> {
  const first = await loader(0, PAGE_SIZE).catch(() => ({ items: [] as T[], total: 0 }));
  if (genRef.current !== gen) return;
  setState(() => first.items);
  let offset = first.items.length;
  let total = first.total;
  if (!offset || offset >= total) return;
  // background continuation — not awaited by the caller
  void (async () => {
    while (offset < total) {
      const page = await loader(offset, PAGE_SIZE).catch(() => null);
      if (!page || !page.items.length || genRef.current !== gen) return;
      setState((cur) => [...cur, ...page.items]);
      offset += page.items.length;
      total = page.total;
    }
  })();
}

export function LibraryProvider({ children }: { children: ReactNode }) {
  const [likedTracks, setLT] = useState<Track[]>([]);
  const [likedAlbums, setLA] = useState<Album[]>([]);
  const [likedArtists, setLR] = useState<Artist[]>([]);
  const genRef = useRef(0);

  const liked = useMemo(() => ({
    tracks: new Set(likedTracks.map(t => t.id)),
    albums: new Set(likedAlbums.map(a => a.id)),
    artists: new Set(likedArtists.map(a => a.id)),
  }), [likedTracks, likedAlbums, likedArtists]);

  const refresh = useCallback(async () => {
    const gen = ++genRef.current;
    await Promise.all([
      loadLazy(musicService.likedTracks, setLT, genRef, gen),
      loadLazy(musicService.likedAlbums, setLA, genRef, gen),
      loadLazy(musicService.likedArtists, setLR, genRef, gen),
    ]);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const toggle = useCallback(async (kind: LibraryKind, id: string) => {
    await musicService.toggleLibrary(kind, id);
    await refresh();
  }, [refresh]);

  const isLiked = useCallback((kind: LibraryKind, id: string) => {
    if (kind === "track") return liked.tracks.has(id);
    if (kind === "album") return liked.albums.has(id);
    if (kind === "artist") return liked.artists.has(id);
    return false;
  }, [liked]);

  return (
    <LibraryContext.Provider value={{ liked, toggle, isLiked, refresh, likedTracks, likedAlbums, likedArtists }}>
      {children}
    </LibraryContext.Provider>
  );
}
